import { Create, useForm } from "@refinedev/antd";
import { HttpError } from "@refinedev/core";
import { DatePicker, Form, Input, InputNumber, Select, Switch } from "antd";
import { ImageUpload } from "../../components/ImageUpload";

/**
 * Create an ad-reel creative. Media is uploaded to S3 (file_upload); the form
 * submits the file ids. Ordering within a tier is `priority`; the house-before-
 * sold rule is enforced server-side. The CTA type/target and poster fields are
 * shown conditionally and normalised before submit so the server's
 * "set-together" guard is satisfied.
 */
export function AdCreativeCreate() {
  const { formProps, saveButtonProps, formLoading } = useForm<any, HttpError, any>({
    resource: "ad-creative",
    successNotification: (data) => ({ message: data?.data.message, type: "success" }),
    action: "create",
  });

  const handleFinish = (values: any) => {
    const v = { ...values };
    if (!v.ctaType) {
      v.ctaType = null;
      v.ctaTarget = null;
    }
    if (v.mediaType === "image") v.posterID = null;
    return formProps.onFinish?.(v);
  };

  return (
    <Create saveButtonProps={saveButtonProps} isLoading={formLoading} title="Create ad creative">
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Form.Item label="Media type" name={["mediaType"]} initialValue="image" rules={[{ required: true }]}>
          <Select
            options={[
              { label: "Image", value: "image" },
              { label: "Video", value: "video" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Media"
          name="mediaID"
          rules={[{ required: true, message: "Upload the creative" }]}
          tooltip="The image canvas, or the video clip."
        >
          <ImageUpload uploadType="AD_CREATIVE" accept="image/*,video/*" />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(p, c) => p.mediaType !== c.mediaType}>
          {({ getFieldValue }) =>
            getFieldValue("mediaType") === "video" ? (
              <Form.Item
                label="Poster (still frame)"
                name="posterID"
                rules={[{ required: true, message: "Video creatives need a poster" }]}
                tooltip="Shown before/without the clip."
              >
                <ImageUpload uploadType="AD_CREATIVE" accept="image/*" />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item label="House inventory (GymLive)" name={["isHouse"]} valuePropName="checked" initialValue={true} tooltip="On = 'From GymLive' + runs before sold slots. Off = 'Sponsored'.">
          <Switch />
        </Form.Item>

        <Form.Item label="Brand" name={["brand"]} rules={[{ required: true }]}>
          <Input placeholder="GymLive" />
        </Form.Item>

        <Form.Item label="Mark" name={["mark"]} rules={[{ required: true }]} tooltip="Short badge, e.g. GL.">
          <Input maxLength={4} placeholder="GL" />
        </Form.Item>

        <Form.Item label="Title" name={["title"]} rules={[{ required: true }]} tooltip="Plain text — the app wraps it.">
          <Input placeholder="Train with the best." />
        </Form.Item>

        <Form.Item label="Subtitle" name={["sub"]}>
          <Input placeholder="S&C programme · built with your club's coaches" />
        </Form.Item>

        <Form.Item label="CTA label" name={["cta"]} rules={[{ required: true }]}>
          <Input placeholder="Join the programme" />
        </Form.Item>

        <Form.Item label="CTA destination" name={["ctaType"]} initialValue="" tooltip="Where tapping the card goes. None = not tappable.">
          <Select
            options={[
              { label: "None (not tappable)", value: "" },
              { label: "In-app screen", value: "in_app" },
              { label: "External URL", value: "external" },
            ]}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(p, c) => p.ctaType !== c.ctaType}>
          {({ getFieldValue }) => {
            const t = getFieldValue("ctaType");
            if (!t) return null;
            return (
              <Form.Item
                label={t === "external" ? "URL" : "Screen key"}
                name="ctaTarget"
                rules={[{ required: true }]}
                tooltip={t === "external" ? "https://…" : "e.g. program, paywall"}
              >
                <Input placeholder={t === "external" ? "https://example.com" : "program"} />
              </Form.Item>
            );
          }}
        </Form.Item>

        <Form.Item label="Priority" name={["priority"]} initialValue={0} tooltip="Order within tier — lower shows first.">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Starts at" name={["startsAt"]} tooltip="Optional. Empty = live now.">
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Ends at" name={["endsAt"]} tooltip="Optional. Empty = never expires.">
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Active" name={["isActive"]} valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
      </Form>
    </Create>
  );
}
